/// <amd-module name="Wit/WorkItemForm/Details/WorkItemFormGroup" />
import { useDerivedObservable } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { useCallback, useState, useContext } from "react";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { Control, Group } from "Wit/Clients/Wit/Contracts/WorkItemFormLayout";
import { WellKnownControlNames } from "Wit/Common/Generated/Constants";
import { useWorkItem } from "Wit/WorkItemForm/Common/Hooks/WorkItemFormPageContext";
import { IWorkItemNotificationListener } from "Wit/WorkItemForm/Contracts/Contracts";
import * as Resources from "Wit/WorkItemForm/Resources";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { isFieldValid } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import * as React from "react";
import { CollapsibleSection } from "../Common/Components";
import { WorkItemFormControl } from "./WorkItemFormControl";
import { WorkItemFormExtensionHost } from "./WorkItemFormExtensionHost";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext } from "VSS/Platform/Layout";
import { getDevelopmentZeroDataExperienceSettings } from "Wit/WorkItemForm/Links/GitHub/GitHubLinkTypeService";
import { DevelopmentZeroDataSettingsResponse } from "../Links/GitHub/GitHubDataProviderContracts";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

export interface IWorkItemFormGroupProps {
    keyPrefix: string;
    group: Group;
    onExtensionLoaded: (workItem: IWorkItem, hostInstance: IWorkItemNotificationListener) => void;
}

export function WorkItemFormGroup({ keyPrefix, group, onExtensionLoaded }: IWorkItemFormGroupProps) {
    const getControlKey = (control: Control) => keyPrefix + "." + control.id;
    const workItem = useWorkItem();
    const pageContext = usePageContext();

    const groupErrorIcon = useDerivedObservable(
        workItem.infoText,
        () => {
            return isGroupInvalid(group, workItem)
                ? {
                      ariaLabel: format(Resources.InvalidTabOrGroup, group.label),
                      className: "padding-left-4 error-text font-size-m",
                      iconName: "AlertSolid",
                      key: "invalid-control-icon"
                  }
                : undefined;
        },
        [group]
    );

    const htmlControl = group.controls.find(control => control.controlType === WellKnownControlNames.HtmlControl);
    const hasHtmlControl = !!htmlControl;

    const isDeploymentGroup =
        group.controls.length > 0 && group.controls.every(control => control.controlType === WellKnownControlNames.DeploymentsControl);
    const isDevelopmentGroup =
        group.controls.length > 0 &&
        group.controls.every(control => control.controlType === WellKnownControlNames.LinksControl && control.id === "Development");

    const [zeroDataExperienceData, setZeroDataExperienceData] = useState<DevelopmentZeroDataSettingsResponse | undefined>(undefined);
    const isHiddenKnownControlGroup = isHiddenWellKnownControlGroup(isDeploymentGroup, isDevelopmentGroup, pageContext, zeroDataExperienceData);

    React.useEffect(() => {
        if (isDeploymentGroup || isDevelopmentGroup) {
            getDevelopmentZeroDataExperienceSettings(pageContext).then(data => {
                setZeroDataExperienceData(data);
            });
        }
    }, []);

    let tooltipText;
    if (htmlControl && htmlControl.id !== undefined) {
        const field = workItem.fieldMap[htmlControl.id.toUpperCase()];
        if (field) {
            tooltipText = format(Resources.FieldNameTooltip, field.fieldDefinition.name);
            const helpText = workItem.getTypeData().rules.fieldHelpTexts[field.fieldDefinition.id];
            if (helpText) {
                tooltipText = helpText + "\n" + tooltipText;
            }
        }
    }

    return (
        <div className={isHiddenKnownControlGroup ? "flex-column work-item-form-group hidden" : "flex-column work-item-form-group"}>
            <CollapsibleSection
                key={keyPrefix}
                label={group.label}
                hasHtmlControl={hasHtmlControl}
                tooltipText={tooltipText}
                iconProps={groupErrorIcon}
                settingsKey={keyPrefix}
            >
                {group.isContribution ? (
                    <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                        <WorkItemFormExtensionHost
                            height={group.contribution.height}
                            contributionId={group.contribution.contributionId}
                            onLoaded={onExtensionLoaded}
                        />
                    </ErrorBoundary>
                ) : (
                    group.controls
                        .filter(c => c.visible)
                        .map(c => {
                            return (
                                <ErrorBoundary handlerComponentType="tfs.page-error-handler" key={getControlKey(c)}>
                                    <WorkItemFormControl
                                        controlKey={getControlKey(c) + "."}
                                        control={c}
                                        groupLength={group.controls.length}
                                        onExtensionLoaded={onExtensionLoaded}
                                    />
                                </ErrorBoundary>
                            );
                        })
                )}
            </CollapsibleSection>
        </div>
    );
}

export function isGroupInvalid(group: Group, workItem: IWorkItem): boolean {
    const fieldLookup = workItem.getFieldLookup();
    return group.controls.some(control => {
        const fieldEntry = fieldLookup[control.id];
        const field = fieldEntry ? workItem.fieldMapById[fieldEntry.id] : undefined;
        return field && !isFieldValid(field);
    });
}

function isHiddenWellKnownControlGroup(
    isDeploymentGroup: boolean,
    isDevelopmentGroup: boolean,
    context: IVssPageContext,
    zeroData: DevelopmentZeroDataSettingsResponse | undefined
): boolean {
    return isFeatureFlagEnabled(context, FeatureFlags.DisableNewWorkItemZeroStateCleanUpBehavior, false)
        ? false
        : (isDeploymentGroup && !isFeatureEnabled(context, "ms.vss-build.pipelines")) ||
              (isDevelopmentGroup && (!zeroData || (!zeroData.showGitHubZeroDataExperience && !zeroData.showAzureReposZeroDataExperience)));
}
